import type { Environment } from '../App'

const API_BASE_URLS: Record<Environment, string> = {
  tst: 'https://tst.apim.suitsupply.com',
  acc: 'https://tst.apim.suitsupply.com',
  prd: 'https://apim.suitsupply.com',
} as const

const WEBSITE_BASE_URLS: Record<Environment, string> = {
  tst: 'https://testing.suitsupply.com',
  acc: 'https://acceptance.suitsupply.com',
  prd: 'https://suitsupply.com',
} as const

const LOCALE_PATH_PATTERN = /^\/([a-z]{2}-[a-z]{2})(?:\/|$)/i
const LOCALE_VALUE_PATTERN = /^[a-z]{2}-[a-z]{2}$/i
const DEFAULT_WEBSITE_LOCALE = 'en-us'

export function getApiBaseUrl(env: Environment): string {
  return API_BASE_URLS[env]
}

export function getWebsiteBaseUrl(env: Environment): string {
  return WEBSITE_BASE_URLS[env]
}

function normalizeLocale(locale?: string): string | undefined {
  if (!locale) return undefined

  const normalized = locale.trim().replace('_', '-').toLowerCase()
  return LOCALE_VALUE_PATTERN.test(normalized) ? normalized : undefined
}

function getCurrentWebsiteLocale(): string {
  if (typeof window === 'undefined') return DEFAULT_WEBSITE_LOCALE

  const localeFromPath =
    window.location.pathname.match(LOCALE_PATH_PATTERN)?.[1]
  const localeFromLang = document.documentElement.lang

  return (
    normalizeLocale(localeFromPath) ??
    normalizeLocale(localeFromLang) ??
    DEFAULT_WEBSITE_LOCALE
  )
}

function getLocalizedWebsiteUrl(
  path: string,
  env: Environment,
  locale = getCurrentWebsiteLocale(),
): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const normalizedLocale = normalizeLocale(locale) ?? DEFAULT_WEBSITE_LOCALE

  return `${getWebsiteBaseUrl(env)}/${normalizedLocale}${normalizedPath}`
}

/**
 * Product detail page for an order item.
 *
 * Locale-prefixed through `getLocalizedWebsiteUrl`, like the FAQ and
 * customer-service links beside it. Without a prefix the storefront's
 * `($locale)` route — an optional segment — matches anyway and falls through to
 * its default locale, so a GB customer tapping a regular item in order details
 * landed on `en-us`. Custom Made items never had this: they carry
 * `countryCode` as a query param instead.
 *
 * The returns link was fixed for the same reason (#6770, #6793); this call site
 * was missed because it only shows up on ready-to-wear items.
 */
export function getProductUrl(
  productCode: string,
  env: Environment,
  locale?: string,
): string {
  return getLocalizedWebsiteUrl(`/men/${productCode}.html`, env, locale)
}

export function getFaqPageUrl(
  env: Environment,
  path = '/overview.html',
  locale?: string,
): string {
  return getLocalizedWebsiteUrl(path, env, locale)
}

export function getCustomerServiceUrl(
  env: Environment,
  locale?: string,
): string {
  return getLocalizedWebsiteUrl('/customer-service.html', env, locale)
}

function formatCustomMadeProductWord(word: string): string {
  const sanitized = word.replace(/[^a-z0-9]/gi, '')
  return sanitized
    ? `${sanitized.charAt(0).toUpperCase()}${sanitized.slice(1)}`
    : ''
}

export function getCustomMadeProductFromName(name: string): string | undefined {
  const productName = name
    .replace(/^custom made\s+/i, '')
    .split(':')[0]
    ?.trim()

  if (!productName) return undefined

  const words = productName.split(/\s+/)
  const tuxedoIndex = words.findIndex((word) => word.toLowerCase() === 'tuxedo')

  if (tuxedoIndex >= 0) {
    const tuxedoProduct = words
      .slice(tuxedoIndex, tuxedoIndex + 2)
      .map(formatCustomMadeProductWord)
      .join('')

    return tuxedoProduct || undefined
  }

  return formatCustomMadeProductWord(words[0] ?? '') || undefined
}

export function getCustomMadeProductUrl(
  configurationId: string,
  countryCode: string,
  env: Environment,
  product?: string,
): string {
  void env
  const query = new URLSearchParams({
    countryCode,
    client: 'onLine',
    configurationId,
    section: 'fabric',
    level: 'group',
    origin: 'normal',
  })

  if (product) {
    query.set('product', product)
  }

  return `/custom-made?${query.toString()}`
}

// In-store Custom Made reorder deep link (RP-5643, in-store / Style Advisor only).
// Opens the configurator pre-filled from a single CM configuration.
//
// Contract verified against the configurator app (apps/custom-made):
//   - Entry path is `/configurator` (NOT `/reorder` — that page is the
//     CustomerID-keyed reorder *list* and ignores configurationId).
//   - `api/configurator/route.ts` reads client / countryCode / origin /
//     configurationId / CustomerID from the query string.
//   - `origin=Reorder` is REQUIRED: without it origin defaults to Normal and
//     `configuratorRequests.ts` skips preloading the configuration (empty flow).
//   - Literal values mirror apps/custom-made/src/utils/constants.tsx
//     (Clients.inStore = 'inStore', ConfiguratorOrigin.Reorder = 'Reorder').
//
// Hosts verified against the configurator app's own env mapping
// (apps/custom-made/src/components/configurator/api/getConfiguratorProps.ts)
// and the custom-made Playwright constants. Note the naming is NOT uniform:
// tst/acc use the `*-v4.custom-made` subdomain, prd is `custommade` (no hyphen).
const IN_STORE_CONFIGURATOR_BASE_URLS: Record<Environment, string> = {
  tst: 'https://testing-v4.custom-made.suitsupply.com',
  acc: 'https://acceptance-v4.custom-made.suitsupply.com',
  prd: 'https://custommade.suitsupply.com',
} as const

export function getInStoreCustomMadeReorderUrl(
  configurationId: string,
  countryCode: string,
  env: Environment,
  customerId: string,
): string {
  const query = new URLSearchParams({
    client: 'inStore',
    countryCode,
    origin: 'Reorder',
    configurationId,
    CustomerID: customerId,
  })
  return `${IN_STORE_CONFIGURATOR_BASE_URLS[env]}/configurator?${query.toString()}`
}

export const apiConfig = {
  getBaseUrl: getApiBaseUrl,
  getWebsiteBaseUrl,
  getFaqPageUrl,
  getCustomerServiceUrl,
  getProductUrl,
  getCustomMadeProductUrl,
  getInStoreCustomMadeReorderUrl,
} as const
